import React, { useEffect, useRef, useState, useCallback } from 'react';
import { api, API_BASE_URL } from '../api/client';
import { computeFocusScore, defaultWeights, computeAchievements } from '../utils/focusScore';
import useSessionManager from './useSessionManager';
import { useCameraPersistence } from './useCameraPersistence';
import type { FaceLandmarker, NormalizedLandmark, ObjectDetector } from '@mediapipe/tasks-vision';
import { computeGraceEndMs } from '../utils/time';
import { initPerfMonitor } from '../utils/perfMonitor';

const wasmBase = '/models';
const modelUrl = '/models/face_landmarker.task';
const objectModelUrl = '/models/efficientdet_lite0.tflite';

const ORIENT_PITCH_RATIO = 2.6; // Scale-invariant ratio (pitch / eyeDist)
const ORIENT_YAW_RATIO = 3.0; // Scale-invariant ratio (yaw / eyeDist)
// Face-not-visible timeout 4s; orientation timeout 8s (Updated by user)
const FACE_TIMEOUT_MS = 4000;
const TURN_TIMEOUT_MS = 8000;
const MIN_EYE_DIST = 0.05;
const MIN_BBOX_W = 0.10;
const MIN_BBOX_H = 0.10;

// Advanced Focus Rules Constants
const EAR_THRESH = 0.25; // Eye Aspect Ratio (closed < 0.25)
const EAR_TIME_MS = 4000; // Relaxed to 4s

const MAR_THRESH = 0.5; // Mouth Aspect Ratio (yawn > 0.5)
const MAR_TIME_MS = 3000; // 3 seconds

const TILT_THRESH = 0.35; // ~20 degrees in radians
const TILT_TIME_MS = 10000; // Relaxed to 10s

const PHONE_HOLD_MS = 6000;
const PHONE_MIN_FRAC = 0.012;
const PHONE_MIN_AR = 1.35;
const ORIENT_FAIL_MS = 4000;
const QUALITY_FAIL_MS = 2500;
const DISTANCE_FAIL_MS = 3000;
const LOOKDOWN_FAIL_MS = 3000;
const DROWSY_MILD_MS = 2000;
const POSTURE_MILD_MS = 3000;

export type PhoneStatus = 'waiting' | 'connected' | 'disconnected' | 'backgrounded';

type CameraMonitorProps = { active?: boolean; sessionId?: string; externalPhonePenaltyMs?: number; phoneStatus?: PhoneStatus; sessionStartMs?: number; inputMediaStream?: MediaStream | null; };
const CameraMonitor: React.FC<CameraMonitorProps> = ({ active = true, sessionId, externalPhonePenaltyMs = 0, phoneStatus = 'waiting', sessionStartMs: propStartMs, inputMediaStream }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pipCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);
  const phoneStatusRef = useRef<PhoneStatus>(phoneStatus);
  const externalPhonePenaltyMsRef = useRef<number>(externalPhonePenaltyMs || 0);
  useEffect(() => { phoneStatusRef.current = phoneStatus; }, [phoneStatus]);
  useEffect(() => { externalPhonePenaltyMsRef.current = externalPhonePenaltyMs || 0; }, [externalPhonePenaltyMs]);

  const pipDrawAnimRef = useRef<number | null>(null);
  const pipDrawActiveRef = useRef<boolean>(false);
  const pipWorkerRef = useRef<Worker | null>(null);
  const pipFaceBoxRef = useRef<{ cx: number; cy: number; w: number; h: number } | null>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const prevIsFocusedRef = useRef<boolean>(false);
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const objectDetectorRef = useRef<ObjectDetector | null>(null);
  const perfMonRef = useRef<ReturnType<typeof initPerfMonitor> | null>(null);

  const lastFaceTsRef = useRef<number>(0);
  const lastFacingTsRef = useRef<number>(0);
  
  // Browser/Tab Monitoring Refs
  const tabAwayMsRef = useRef<number>(0);
  const closedTotalMsRef = useRef<number>(0); // Renamed from hiddenTotalMs for Pause logic
  const tabHiddenStartRef = useRef<number | null>(null);

  // removed: twoHandStartTsRef

  const drowsyStartTsRef = useRef<number>(0);
  const yawnStartTsRef = useRef<number>(0);
  const yawnPenaltyMsRef = useRef<number>(0); // Fixed 10s penalty per yawn instance
  const prevIsYawningRef = useRef<boolean>(false);
  const postureStartTsRef = useRef<number>(0);
  const qualityFailStartTsRef = useRef<number>(0);
  const orientFailStartTsRef = useRef<number>(0);
  const distanceFailStartTsRef = useRef<number>(0);
  const lookDownStartTsRef = useRef<number>(0);

  const lastObjectCheckRef = useRef<number>(0);
  const phoneDetectedStartTsRef = useRef<number>(0);

  const intervalRef = useRef<number | null>(null);
  const [debug, setDebug] = useState<{
    face: boolean; quality: boolean; orient: boolean;
    yaw: number; pitch: number; roll: number;
    eye: number; ear: number; mar: number;
    bboxW: number; bboxH: number;
    sinceFace: number; sinceOrient: number;
    vw: number; vh: number; ready: number; delegate: string;
    isDrowsy: boolean; isYawning: boolean; badPosture: boolean;
    detectedObjects: string[]; isPhoneDistraction: boolean;
    poorQuality: boolean; tooFar: boolean; orientFail: boolean; lookDown: boolean;
    contrast: number; focusConfidence: number; fps: number;
  }>({
    face: false, quality: false, orient: false,
    yaw: 0, pitch: 0, roll: 0,
    eye: 0, ear: 0, mar: 0,
    bboxW: 0, bboxH: 0,
    sinceFace: 0, sinceOrient: 0,
    vw: 0, vh: 0, ready: 0, delegate: 'unknown',
    isDrowsy: false, isYawning: false, badPosture: false,
    detectedObjects: [], isPhoneDistraction: false,
    poorQuality: false, tooFar: false, orientFail: false, lookDown: false,
    contrast: 0, focusConfidence: 0, fps: 0
  });
  const [started, setStarted] = useState<boolean>(active);
  const [onBreak, setOnBreak] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const delegateRef = useRef<string>('unknown');
  const multiMonitorRef = useRef<boolean>(true);
  const [pipMsg, setPipMsg] = useState<string>('');
  const [pipComposedActive, setPipComposedActive] = useState<boolean>(false);
  const pipComposedActiveRef = useRef<boolean>(false);
  useEffect(() => { pipComposedActiveRef.current = pipComposedActive; }, [pipComposedActive]);

  // Auto-clear PiP message
  useEffect(() => {
    if (pipMsg) {
      const id = setTimeout(() => setPipMsg(''), 5000);
      return () => clearTimeout(id);
    }
  }, [pipMsg]);

  // Workflow State: 'init' -> 'camera' -> 'cv' -> 'ready'
  const [workflowStage, setWorkflowStage] = useState<'init' | 'camera' | 'cv' | 'ready'>('init');

  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const smRef = useRef<any>(null);
  const isFocusedRef = useRef<boolean>(false);
  const focusStateRef = useRef<string>('Not Focused');
  const debugRef = useRef<any>(null);
  const cameraOffModeRef = useRef<boolean>(false);
  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const phoneEvidenceMsRef = useRef<number>(0);
  const contrastEmaRef = useRef<number>(0);
  const emaAlphaRef = useRef<number>(0.8);
  const focusConfidenceSeriesRef = useRef<Array<{ t: number; v: number }>>([]);
  const contrastSeriesRef = useRef<Array<{ t: number; v: number }>>([]);
  const distanceSeriesRef = useRef<Array<{ t: number; v: number }>>([]);
  const lastDebugUpdateRef = useRef<number>(0);
  const logoImgRef = useRef<HTMLImageElement | null>(null);
  const mirrorCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const mirrorAnimRef = useRef<number | null>(null);
  const focusWeightRef = useRef<number>(1);
  const getFocusWeight = useCallback(() => focusWeightRef.current, []);
  const lastLoopMsRef = useRef<number>(Date.now());
  const awayMsRef = useRef<number>(0);
  const phoneMsRef = useRef<number>(0);
  const drowsyMsRef = useRef<number>(0);
  const postureMsRef = useRef<number>(0);
  const notFocusedMsRef = useRef<number>(0);
  const cameraOffMsRef = useRef<number>(0);
  const poorQualityMsRef = useRef<number>(0);
  const tooFarMsRef = useRef<number>(0);
  const orientFailMsRef = useRef<number>(0);
  const lookDownMsRef = useRef<number>(0);
  const sustainedFocusMsRef = useRef<number>(0);
  const totalFocusMsRef = useRef<number>(0);
  const focusHistoryRef = useRef<Array<{ timestamp: number; isFocused: boolean; state: string; details?: string }>>([]);
  const lastHistoryUpdateRef = useRef<number>(0);
  const lastLocalSaveRef = useRef<number>(0);
  const lastFocusStartRef = useRef<number>(0);
  const hiddenStartRef = useRef<number>(0);
  const closingRef = useRef<boolean>(false);
  const isMobileRef = useRef<boolean>(false);
  const fpsEmaRef = useRef<number>(0);
  const lastFrameTsRef = useRef<number>(0);
  const lastScoreRef = useRef<number>(0);
  const earEmaRef = useRef<number>(0);
  const marEmaRef = useRef<number>(0);
  const tickRef = useRef<(() => void) | null>(null);
  
  // Adaptive Framerate Refs
  const lastTickRef = useRef<number>(Date.now());
  const rafIdRef = useRef<number | null>(null);



  useEffect(() => {
    const pv = pipVideoRef.current;
    if (!pv) return;
    const onLeave = () => {
      setPipComposedActive(false);
      pipDrawActiveRef.current = false;
      if (pipDrawAnimRef.current) cancelAnimationFrame(pipDrawAnimRef.current);
      try { pv.pause(); } catch {}
    };
    pv.addEventListener('leavepictureinpicture', onLeave);
    
    const onModeChange = (e: Event) => {
      const target = e.target as any;
      if (target && target.webkitPresentationMode === 'inline') {
        onLeave();
      }
    };
    pv.addEventListener('webkitpresentationmodechanged', onModeChange);
    const v = videoRef.current;
    if (v) v.addEventListener('webkitpresentationmodechanged', onModeChange);

    return () => {
      pv.removeEventListener('leavepictureinpicture', onLeave);
      pv.removeEventListener('webkitpresentationmodechanged', onModeChange);
      if (v) v.removeEventListener('webkitpresentationmodechanged', onModeChange);
    };
  }, []);

  useEffect(() => {
    multiMonitorRef.current = true;
  }, []);

  // Suppress noisy Mediapipe WASM INFO messages that are printed to stderr as errors
  useEffect(() => {
    const prevErr = console.error;
    const filterErr = (msg?: any, ...args: any[]) => {
      try {
        const s = typeof msg === 'string' ? msg : String(msg || '');
        if (/INFO:\s*Created TensorFlow Lite XNNPACK delegate/i.test(s)) {
          // console.info(s);
          return;
        }
      } catch {}
      prevErr(msg, ...args);
    };
    console.error = filterErr as any;
    return () => { console.error = prevErr; };
  }, []);

  useEffect(() => { setStarted(active); }, [active]);

  // Pre-initialize PiP stream to ensure user activation token is valid when toggled
  useEffect(() => {
    if (!started) return;
    const pv = pipVideoRef.current;
    const pc = pipCanvasRef.current;
    if (pv && pc && !pv.srcObject) {
        // Ensure canvas has dimensions
        if (pc.width < 10) pc.width = 300;
        if (pc.height < 10) pc.height = 300;
        const stream = (pc as any).captureStream ? (pc as any).captureStream(30) : null;
        if (stream) {
            pv.srcObject = stream;
            pv.muted = true;
            pv.play().catch(() => {});
        }
    }
  }, [started]);


  // Load initial state from local storage for resilience
  const isRestored = useCameraPersistence(sessionId, {
    tabAwayMsRef,
    phoneMsRef,
    awayMsRef,
    notFocusedMsRef,
    drowsyMsRef,
    postureMsRef,
    sustainedFocusMsRef,
    totalFocusMsRef,
    focusHistoryRef,
    closedTotalMsRef,
    yawnPenaltyMsRef
  });
  // Backward compatibility for existing code using isRestored state
  // const [isRestored, setIsRestored] = useState<boolean>(false); -> replaced by hook return

  // Sync Logic
  const syncMetrics = useCallback(async (opts?: { isUnload?: boolean }) => {
      const isUnload = opts?.isUnload || false;
      if (!sessionId || !isRestored) return;
      let startMs = propStartMs || (typeof window !== 'undefined' && (window as any).__sessionStartMs) || 0;
      if (!startMs && sessionId) {
       try {
         const t = JSON.parse(localStorage.getItem(`sessionTimes:${sessionId}`) || '{}');
         if (t.startMs) startMs = t.startMs;
       } catch {}
    }
    if (!startMs) return;
    const elapsedMs = startMs ? Math.max(0, Date.now() - startMs) : 0;
    
    const endRaw = (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined;
    const endMs = (startMs && endRaw) ? computeGraceEndMs(startMs, endRaw) : undefined;
    
    // Focus Score is the single source of truth.

    // Calculate Partial Attendance (Gaps) from History to sync with Server
    // This ensures that "hidden_total_seconds" on server matches the gaps seen in the timeline
    let calculatedPartialMs = 0;
    const history = focusHistoryRef.current;
    if (history && history.length > 0) {
        // Sort by timestamp
        const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp);
        
        // 1. Initial Gap
        if (startMs && sorted[0].timestamp - startMs > 30000) {
            calculatedPartialMs += (sorted[0].timestamp - startMs);
        }

        // 2. Inter-event Gaps
        for (let i = 1; i < sorted.length; i++) {
            const diff = sorted[i].timestamp - sorted[i-1].timestamp;
            if (diff > 30000) {
                calculatedPartialMs += diff;
            }
        }

        // 3. Tail Gap (End of Session)
        // If the session has ended, and the last history entry is far from the end,
        // it means the user left/suspended before the end.
        if (endMs && Date.now() > endMs) {
             const lastTs = sorted[sorted.length - 1].timestamp;
             if (endMs - lastTs > 30000) {
                 calculatedPartialMs += (endMs - lastTs);
             }
        }
    }
    // Update ref so it persists
    closedTotalMsRef.current = calculatedPartialMs;

    const metrics = {
        phoneMs: phoneMsRef.current + (externalPhonePenaltyMs || 0),
        awayMs: awayMsRef.current,
        tabAwayMs: tabAwayMsRef.current,
        closedTotalMs: closedTotalMsRef.current,
        // Legacy support for server
        hiddenTotalMs: closedTotalMsRef.current,
        notFocusedMs: notFocusedMsRef.current,
        drowsyMs: drowsyMsRef.current,
        postureMs: postureMsRef.current,
        yawnPenaltyMs: yawnPenaltyMsRef.current,
        sustainedFocusMs: sustainedFocusMsRef.current,
        totalFocusMs: totalFocusMsRef.current,
        elapsedMs,
    };
    const data = {
        ts: Date.now(),
        metrics,
        history: focusHistoryRef.current
    };
    
    // Local persistence
    try {
        localStorage.setItem(`sessionMonitor:${sessionId}`, JSON.stringify(data));
    } catch (e) {
        // LocalStorage save failed
    }

    // Server sync
    try {
        if (!isUnload) window.dispatchEvent(new CustomEvent('score-sync-start'));

        if (isUnload) {
            // Robust sync for unload/pagehide using fetch keepalive
            const token = localStorage.getItem('token');
            const url = `${API_BASE_URL}/session/${sessionId}/sync`;
            const headers: any = {
                'Content-Type': 'application/json'
            };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            
            fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({ metrics, history: focusHistoryRef.current }),
                keepalive: true
            }).catch(err => console.error("Unload sync failed", err));
            
            return;
        }

        const res = await api.post(`/session/${sessionId}/sync`, {
            metrics,
            history: focusHistoryRef.current
        }, { skipAuthRedirect: true } as any);

        // Discrepancy detection
        if (res?.data?.metrics) {
            const sTotal = Number(res.data.metrics.totalFocusMs) || 0;
            const lTotal = Number(metrics.totalFocusMs) || 0;
            // 5 second tolerance
            if (Math.abs(sTotal - lTotal) > 5000) {
                 window.dispatchEvent(new CustomEvent('score-sync-discrepancy', {
                    detail: {
                        message: `Sync mismatch: Server ${Math.round(sTotal/1000)}s vs Local ${Math.round(lTotal/1000)}s`,
                        server: sTotal,
                        local: lTotal
                    }
                 }));
            }
        }

        window.dispatchEvent(new CustomEvent('score-sync-success'));
    } catch (e: any) {
        console.warn('Server sync failed', e);
        const msg = e.response?.data?.message || e.message || 'Sync failed';
        window.dispatchEvent(new CustomEvent('score-sync-error', { detail: { message: msg } }));
    }
    }, [sessionId, isRestored, externalPhonePenaltyMs, propStartMs]);

  // Tab Monitoring
  useEffect(() => {
    const handleVis = () => {
      const now = Date.now();
      if (document.hidden) {
        tabHiddenStartRef.current = now;
        // Force sync when hiding to capture latest state before potential close
        syncMetrics({ isUnload: true });
      } else {
        // Returned to foreground
        // CRITICAL: Check if we were actually running in the background (Adaptive FPS)
        // If the loop was running (last loop < 3s ago), we DO NOT want to apply a bulk "Away/Hidden" penalty.
        // We only apply penalties for time where the loop was truly suspended/throttled.
        const lastLoop = lastLoopMsRef.current || 0;
        const timeSinceLastLoop = now - lastLoop;
        
        // If we were running recently (within 3s, allowing for 1FPS + jitter), 
        // then the background detection covered this period. No "blind" penalty needed.
        const wasRunning = timeSinceLastLoop < 3000;

        if (tabHiddenStartRef.current) {
          // If we were suspended, the "blind" duration is the gap since the last loop.
          // If we were running, the gap is 0 (effectively).
          const diff = wasRunning ? 0 : timeSinceLastLoop;
          
          if (diff > 1000) {
            
            // CHANGED: Do NOT automatically credit Focus Time or backfill history.
            // If background detection runs, we have real data.
            // If browser suspends, we leave it as a gap (Partial Attendance).
            // This ensures "Away" time is not converted to "Focus Time".
            
            /*
            // Tab Switch/Minimize: ALWAYS treat as Focus Time (No Penalty), regardless of duration
            // "I don't want to include minimizing the window and switching tabs"
            totalFocusMsRef.current += diff;

            // Backfill history so timeline shows "Focused" (Green) instead of "Partial Attendance" (Gap/Purple)
            let t = lastLoop + 1000;
            while (t < now) {
                focusHistoryRef.current.push({
                    timestamp: t,
                    isFocused: true,
                    state: 'focused',
                    details: 'focused'
                });
                t += 1000;
            }
            // Update lastHistoryUpdateRef to prevent duplicate entries
            if (t > 1000) lastHistoryUpdateRef.current = t - 1000;
            */
          }
          tabHiddenStartRef.current = null;
        }
        // Force sync on return
        syncMetrics();
      }
    };
    
    const onBlur = () => {
        if (!document.hidden && !tabHiddenStartRef.current) {
             tabHiddenStartRef.current = Date.now();
        }
    };
    const onFocus = () => {
        if (tabHiddenStartRef.current) {
             const now = Date.now();
             const lastLoop = lastLoopMsRef.current || 0;
             const timeSinceLastLoop = now - lastLoop;
             const wasRunning = timeSinceLastLoop < 3000; // Loop active?

             // Only penalize the gap where the loop was NOT running
             const diff = wasRunning ? 0 : timeSinceLastLoop;

             if (diff > 1000) {

                 // CHANGED: Do NOT automatically credit Focus Time or backfill history.
                 /*
                 // Tab Switch/Minimize: ALWAYS treat as Focus Time (No Penalty)
                 totalFocusMsRef.current += diff;

                 // Backfill history so timeline shows "Focused" (Green) instead of "Partial Attendance" (Gap/Purple)
                 let t = lastLoop + 1000;
                 while (t < now) {
                     focusHistoryRef.current.push({
                         timestamp: t,
                         isFocused: true,
                         state: 'focused',
                         details: 'focused'
                     });
                     t += 1000;
                 }
                 // Update lastHistoryUpdateRef to prevent duplicate entries
                 if (t > 1000) lastHistoryUpdateRef.current = t - 1000;
                 */
             }
             tabHiddenStartRef.current = null;
        }
    };

    const onUnload = () => syncMetrics({ isUnload: true });

    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    window.addEventListener('beforeunload', onUnload);
    
    return () => {
        document.removeEventListener('visibilitychange', handleVis);
        window.removeEventListener('blur', onBlur);
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('beforeunload', onUnload);
        onUnload(); // Save on unmount
    };
  }, [syncMetrics]);

  // Periodic Sync
  useEffect(() => {
    if (!sessionId) return;
    const interval = setInterval(() => {
        syncMetrics();
    }, 5000);
    return () => clearInterval(interval);
  }, [sessionId, syncMetrics]);

  useEffect(() => {
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
      const mobileUa = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
      const touch = typeof window !== 'undefined' ? ('ontouchstart' in window || (navigator as any).maxTouchPoints > 1) : false;
      const smallScreen = typeof window !== 'undefined' ? Math.min(window.screen.width || 0, window.screen.height || 0) < 768 : false;
      isMobileRef.current = !!(mobileUa || touch || smallScreen);
    } catch { isMobileRef.current = false; }
  }, []);


  useEffect(() => {
    if (!started) return;
    let stream: MediaStream | null = null;
    let stopped = false;

    // Core detection function (single pass)
    const runDetection = () => {
      if ((window as any).__debugMonitor) console.log('RunDetection start. Stopped:', stopped);
      if (stopped) return;

      const nowPerf = performance.now();
      if (lastFrameTsRef.current === 0) lastFrameTsRef.current = nowPerf;
      const dtp = Math.max(1, nowPerf - lastFrameTsRef.current);
      lastFrameTsRef.current = nowPerf;

      const nowMs = Date.now();

      // FIX: Run detection even if hidden/backgrounded to track "Away" vs "Focused".
      // We rely on the background loop (setInterval) to call this.
      /* 
      if (document.hidden && !pipComposedActiveRef.current) {
         return; 
      }
      */

      const cycleStartPerf = performance.now();
      const vEl = videoRef.current;
      const lm = landmarkerRef.current;
      const oDet = objectDetectorRef.current;
      
      if (!vEl) {
          if ((window as any).__debugMonitor) console.log('RunDetection: No video ref');
      }
      if (!lm) {
          if ((window as any).__debugMonitor) console.log('RunDetection: No landmarker ref');
      }

      let face = false, quality = false, orient = false;
      let yaw = 0, pitch = 0, roll = 0, eye = 0, bboxW = 0, bboxH = 0;
      let ear = 0, mar = 0;
      let vw = 0, vh = 0, ready = 0;


      let isDrowsy = false;
      let isYawning = false;
      let badPosture = false;
      let poorQuality = false;
      let tooFar = false;
      let orientFail = false;
      let lookDown = false;

      let detectedObjects: string[] = [];
      let isPhoneDistraction = false;

      // dtp calculated above
      const instFps = 1000 / dtp;
      fpsEmaRef.current = fpsEmaRef.current === 0 ? instFps : fpsEmaRef.current + emaAlphaRef.current * (instFps - fpsEmaRef.current);

      if (cameraOffModeRef.current) {
        const dt = Math.max(0, Math.min(1000, nowMs - (lastLoopMsRef.current || nowMs)));
        cameraOffMsRef.current += dt;
        lastLoopMsRef.current = nowMs;
        if (nowMs - lastHistoryUpdateRef.current >= 1000) {
          lastHistoryUpdateRef.current = nowMs;
          focusHistoryRef.current.push({
            timestamp: nowMs,
            isFocused: false,
            state: 'distracted',
            details: 'camera_off'
          });
        }
        setDebug(prev => ({
          ...prev,
          face: false,
          quality: false,
          orient: false,
          bboxW: 0,
          bboxH: 0,
          sinceFace: Math.floor((nowMs - lastFaceTsRef.current) / 1000),
          sinceOrient: Math.floor((nowMs - lastFacingTsRef.current) / 1000),
          vw: 0,
          vh: 0,
          ready: 0,
          delegate: 'none',
          isDrowsy: false,
          isYawning: false,
          badPosture: false,
          detectedObjects: [],
          isPhoneDistraction: false,
          poorQuality: false,
          tooFar: false,
          orientFail: false,
          lookDown: false,
          contrast: 0,
          focusConfidence: 0,
          fps: 1
        }));
      } else if (vEl) {
        vw = vEl.videoWidth;
        vh = vEl.videoHeight;
        ready = vEl.readyState;
        
        if ((window as any).__debugMonitor) {
            console.log(`Video State: ready=${ready}, w=${vw}, h=${vh}`);
        }

        if (vEl.readyState >= 2 && vw > 0 && vh > 0) {
          const now = performance.now();

          // Face Detection
          let primaryFaceBox: { minX: number, minY: number, maxX: number, maxY: number, w: number, h: number, cx: number, cy: number } | null = null;

          if (lm) {
            const res = lm.detectForVideo(vEl, now);
            const faces = (res && res.faceLandmarks) ? res.faceLandmarks : [];

            if (faces.length > 0) {
              face = true;
              
              // Select Primary Face (Score = Area * Centrality)
              // Prioritizes main subject based on size and position
              let maxScore = -1;
              let primaryIdx = 0;
              
              const getBox = (l: NormalizedLandmark[]) => {
                let minX = 1, minY = 1, maxX = 0, maxY = 0;
                for (const p of l) {
                  if (p.x < minX) minX = p.x;
                  if (p.x > maxX) maxX = p.x;
                  if (p.y < minY) minY = p.y;
                  if (p.y > maxY) maxY = p.y;
                }
                return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
              };

              for(let i=0; i<faces.length; i++) {
                const b = getBox(faces[i]);
                const area = b.w * b.h;
                const dist = Math.sqrt(Math.pow(b.cx - 0.5, 2) + Math.pow(b.cy - 0.5, 2));
                // Score favors large area (proximity) and center alignment
                // Multiplier (1.2 - dist) ranges from ~0.5 (corner) to 1.2 (center)
                const score = area * (1.2 - dist);

                if (score > maxScore) {
                  maxScore = score;
                  primaryIdx = i;
                  primaryFaceBox = b;
                }
              }

              const f = faces[primaryIdx] as NormalizedLandmark[];
              const q = measureFaceQuality(f);
              quality = q.ok; eye = q.eye; bboxW = q.bboxW; bboxH = q.bboxH;
              if (quality) {
                lastFaceTsRef.current = nowMs;
                const o = measureOrientation(f);
                yaw = o.yaw; pitch = o.pitch; roll = o.roll; orient = o.ok;
                if (orient) {
                  lastFacingTsRef.current = nowMs;
                }

                // Advanced Rules Checks
                ear = calculateEAR(f);
                mar = calculateMAR(f);
                earEmaRef.current = earEmaRef.current === 0 ? ear : earEmaRef.current + emaAlphaRef.current * (ear - earEmaRef.current);
                marEmaRef.current = marEmaRef.current === 0 ? mar : marEmaRef.current + emaAlphaRef.current * (mar - marEmaRef.current);
                const earUsed = earEmaRef.current || ear;
                const marUsed = marEmaRef.current || mar;
                const isMob = !!isMobileRef.current;
                const earThresh = isMob ? 0.22 : EAR_THRESH; // Increased from 0.18 to 0.22 for better detection
                const earTimeMs = isMob ? 5000 : EAR_TIME_MS; // Reduced from 8s to 5s for faster detection

                if (earUsed < earThresh) {
                  if (drowsyStartTsRef.current === 0) drowsyStartTsRef.current = nowMs;
                  if (nowMs - drowsyStartTsRef.current > earTimeMs) isDrowsy = true;
                } else {
                  drowsyStartTsRef.current = 0;
                }

                // Yawning (MAR)
                const marTimeMs = MAR_TIME_MS; // Use same timing as desktop for mobile
                if (marUsed > MAR_THRESH) {
                  if (yawnStartTsRef.current === 0) yawnStartTsRef.current = nowMs;
                  if (nowMs - yawnStartTsRef.current > marTimeMs) {
                     isYawning = true;
                     // Yawn Penalty: 10s per instance
                     if (!prevIsYawningRef.current) {
                        yawnPenaltyMsRef.current += 10000;
                     }
                  }
                } else {
                  yawnStartTsRef.current = 0;
                }

                const tiltTimeMs = isMob ? 5000 : TILT_TIME_MS; // Faster detection for mobile posture (5s vs 10s)
                if (Math.abs(roll) > TILT_THRESH) {
                  if (postureStartTsRef.current === 0) postureStartTsRef.current = nowMs;
                  if (nowMs - postureStartTsRef.current > tiltTimeMs) badPosture = true;
                } else {
                  postureStartTsRef.current = 0;
                }

                if (!quality) {
                  if (qualityFailStartTsRef.current === 0) qualityFailStartTsRef.current = nowMs;
                } else {
                  qualityFailStartTsRef.current = 0;
                }

                if (!orient) {
                  if (orientFailStartTsRef.current === 0) orientFailStartTsRef.current = nowMs;
                } else {
                  orientFailStartTsRef.current = 0;
                }

                {
                  const isMob2 = !!isMobileRef.current;
                  const minEye = isMob2 ? 0.045 : MIN_EYE_DIST;
                  const minBw = isMob2 ? 0.08 : MIN_BBOX_W;
                  const minBh = isMob2 ? 0.08 : MIN_BBOX_H;
                  if (bboxW < minBw || bboxH < minBh || eye < minEye) {
                  if (distanceFailStartTsRef.current === 0) distanceFailStartTsRef.current = nowMs;
                  } else {
                  distanceFailStartTsRef.current = 0;
                  }
                }

                if (pitch > eye * ORIENT_PITCH_RATIO * 1.5) {
                  if (lookDownStartTsRef.current === 0) lookDownStartTsRef.current = nowMs;
                } else {
                  lookDownStartTsRef.current = 0;
                }

                if (qualityFailStartTsRef.current > 0 && nowMs - qualityFailStartTsRef.current > QUALITY_FAIL_MS) poorQuality = true;
                if (orientFailStartTsRef.current > 0 && nowMs - orientFailStartTsRef.current > ORIENT_FAIL_MS) orientFail = true;
                if (distanceFailStartTsRef.current > 0 && nowMs - distanceFailStartTsRef.current > DISTANCE_FAIL_MS) tooFar = true;
                if (lookDownStartTsRef.current > 0 && nowMs - lookDownStartTsRef.current > LOOKDOWN_FAIL_MS) lookDown = true;
              }
              draw(quality ? f : null);
            } else {
              draw(null);
              // Reset timers if face lost
              drowsyStartTsRef.current = 0;
              yawnStartTsRef.current = 0;
              postureStartTsRef.current = 0;
              qualityFailStartTsRef.current = 0;
              orientFailStartTsRef.current = 0;
              distanceFailStartTsRef.current = 0;
              lookDownStartTsRef.current = 0;
            }
          } else {
            draw(null);
          }

          

          // Object Detection (Throttled to 500ms)
          if (oDet && (nowMs - lastObjectCheckRef.current > 500)) {
            lastObjectCheckRef.current = nowMs;
            const oRes = oDet.detectForVideo(vEl, now);
            if (oRes.detections) {
              const phones = oRes.detections.filter(d => {
                const isPhone = d.categories.some(c => {
                  const name = String(c.categoryName || '').toLowerCase();
                  return name.includes('phone') || name.includes('cell');
                });
                if (!isPhone) return false;
                if (!d.boundingBox || vw <= 0 || vh <= 0) return false;

                const bx = d.boundingBox.originX / vw;
                const by = d.boundingBox.originY / vh;
                const bw = d.boundingBox.width / vw;
                const bh = d.boundingBox.height / vh;
                const bcx = bx + bw / 2;
                const bcy = by + bh / 2;
                const areaFrac = Math.max(0, Math.min(1, (bw * bh)));
                const ar = bw / Math.max(1e-6, bh);
                const arSym = Math.max(ar, 1 / Math.max(ar, 1e-6));

                if (areaFrac < PHONE_MIN_FRAC) return false;
                if (arSym < PHONE_MIN_AR) return false;

                if (primaryFaceBox) {
                  const roiMinX = primaryFaceBox.cx - primaryFaceBox.w * 1.8;
                  const roiMaxX = primaryFaceBox.cx + primaryFaceBox.w * 1.8;
                  const roiMinY = primaryFaceBox.minY - 0.2;
                  const roiMaxY = primaryFaceBox.maxY + 1.5;
                  const inRoi = bcx >= roiMinX && bcx <= roiMaxX && bcy >= roiMinY && bcy <= roiMaxY;
                  if (!inRoi) return false;
                }
                return true;
              });

              let anyPhone = phones.length > 0;

              if (anyPhone) {
                if (phoneDetectedStartTsRef.current === 0) phoneDetectedStartTsRef.current = nowMs;
                phoneEvidenceMsRef.current = nowMs;
              }

              // Collect all detected objects for debug
              detectedObjects = oRes.detections.map(d => (d.categories[0] && d.categories[0].categoryName) || 'object');
            }
          }

          {
            const ac = analysisCanvasRef.current || document.createElement('canvas');
            analysisCanvasRef.current = ac;
            if (ac.width !== vw) ac.width = vw;
            if (ac.height !== vh) ac.height = vh;
            const actx = ac.getContext('2d');
            if (actx) {
              actx.drawImage(vEl, 0, 0, vw, vh);
              const img = actx.getImageData(0, 0, vw, vh);
              const data = img.data;
              const lumAt = (px: number, py: number) => {
                const idx = ((py * vw) + px) * 4;
                const r = data[idx], g = data[idx + 1], b = data[idx + 2];
                return 0.2126 * r + 0.7152 * g + 0.0722 * b;
              };
              const stepX = Math.max(1, Math.floor(vw / 24));
              const stepY = Math.max(1, Math.floor(vh / 24));
              let edges = 0, edgeCount = 0;
              for (let px = 0; px < vw; px += stepX) { edges += lumAt(px, 0) + lumAt(px, vh - 1); edgeCount += 2; }
              for (let py = 0; py < vh; py += stepY) { edges += lumAt(0, py) + lumAt(vw - 1, py); edgeCount += 2; }
              let center = 0, centerCount = 0;
              const sx0 = Math.floor(vw * 0.25), sx1 = Math.floor(vw * 0.75);
              const sy0 = Math.floor(vh * 0.25), sy1 = Math.floor(vh * 0.75);
              for (let px = sx0; px < sx1; px += stepX) {
                for (let py = sy0; py < sy1; py += stepY) { center += lumAt(px, py); centerCount++; }
              }
              let diagEdges = 0, diagCount = 0;
              const m = Math.min(vw, vh);
              const diagStep = Math.max(1, Math.floor(m / 24));
              for (let d = 0; d < m; d += diagStep) {
                diagEdges += lumAt(d, d);
                diagEdges += lumAt(vw - 1 - d, d);
                diagEdges += lumAt(d, vh - 1 - d);
                diagEdges += lumAt(vw - 1 - d, vh - 1 - d);
                diagCount += 4;
              }
              const edgeAvg = edges / Math.max(1, edgeCount);
              const diagAvg = diagEdges / Math.max(1, diagCount);
              const centerAvg = center / Math.max(1, centerCount);
              const contrastCombined = Math.max(Math.abs(edgeAvg - centerAvg), Math.abs(diagAvg - centerAvg));
              contrastEmaRef.current = contrastEmaRef.current === 0 ? contrastCombined : contrastEmaRef.current + emaAlphaRef.current * (contrastCombined - contrastEmaRef.current);
            }
          }

          // Check phone timer with hold/persistence window
          const seenRecently = phoneEvidenceMsRef.current > 0 && (nowMs - phoneEvidenceMsRef.current < PHONE_HOLD_MS);
          // const gatingMet = phoneDetectedStartTsRef.current > 0 && (nowMs - phoneDetectedStartTsRef.current > PHONE_TIME_MS);
          const isZenDistraction = phoneStatusRef.current === 'backgrounded' || phoneStatusRef.current === 'disconnected';
          isPhoneDistraction = isZenDistraction;
          if (!seenRecently) {
            phoneDetectedStartTsRef.current = 0;
          }
        }
      }

      const frozen =
        !!((typeof window !== 'undefined' && (window as any).__freezeMetrics));
        // || (typeof document !== 'undefined' && document.hidden);
      
      const sinceFace = Math.floor((nowMs - lastFaceTsRef.current) / 1000);
      const sinceOrient = Math.floor((nowMs - lastFacingTsRef.current) / 1000);
      const noFace = nowMs - lastFaceTsRef.current > FACE_TIMEOUT_MS;
      const turned = nowMs - lastFacingTsRef.current > TURN_TIMEOUT_MS;

      const dDur = drowsyStartTsRef.current > 0 ? (nowMs - drowsyStartTsRef.current) : 0;
      const pDur = postureStartTsRef.current > 0 ? (nowMs - postureStartTsRef.current) : 0;
      let fWeight = 1;
      if (dDur >= DROWSY_MILD_MS && dDur < EAR_TIME_MS) fWeight = Math.min(fWeight, 0.5);
      if (pDur >= POSTURE_MILD_MS && pDur < TILT_TIME_MS) fWeight = Math.min(fWeight, 0.7);
      if (isDrowsy || badPosture) fWeight = 0;
      focusWeightRef.current = fWeight;
      const focusedNow = !(noFace || turned || isDrowsy || isYawning || badPosture || isPhoneDistraction || poorQuality || tooFar || orientFail || lookDown);
      
      let stateReason = 'Focused';
      if (!focusedNow) {
          if (isPhoneDistraction) stateReason = 'Phone';
          else if (isYawning) stateReason = 'Yawn';
          else if (isDrowsy) stateReason = 'Drowsy';
          else if (badPosture) stateReason = 'Posture';
          else if (noFace || turned) stateReason = 'Away';
          else if (lookDown) stateReason = 'Looking Down';
          else if (tooFar) stateReason = 'Too Far';
          else if (poorQuality) stateReason = 'Low Quality';
          else if (orientFail) stateReason = 'Turned Away';
          else stateReason = 'Not Focused';
      }
      focusStateRef.current = stateReason;
      const bboxArea = Math.max(0, Math.min(1, bboxW * bboxH));
      let focusConfidenceVal = 0;
      {
        const isMob = !!isMobileRef.current;
        const minEye = isMob ? 0.045 : MIN_EYE_DIST;
        const denomEye = isMob ? (0.23 - minEye) : (0.25 - minEye);
        const contrastDiv = isMob ? 24 : 30;
        const eyeNorm = Math.max(0, Math.min(1, (eye - minEye) / Math.max(1e-6, denomEye)));
        const contrastNorm = Math.max(0, Math.min(1, contrastEmaRef.current / contrastDiv));
        const orientNorm = orient ? 1 : 0;
        const qualityNorm = quality ? 1 : 0;
        focusConfidenceVal = Math.max(0, Math.min(100, Math.round(100 * (0.35 * contrastNorm + 0.25 * bboxArea + 0.2 * eyeNorm + 0.1 * orientNorm + 0.1 * qualityNorm))));
        if (!frozen) {
          focusConfidenceSeriesRef.current.push({ t: nowMs, v: focusConfidenceVal });
          contrastSeriesRef.current.push({ t: nowMs, v: Math.round(contrastEmaRef.current) });
          distanceSeriesRef.current.push({ t: nowMs, v: Math.round(eye * 100) });
          if (focusConfidenceSeriesRef.current.length > 300) focusConfidenceSeriesRef.current.shift();
          if (contrastSeriesRef.current.length > 300) contrastSeriesRef.current.shift();
          if (distanceSeriesRef.current.length > 300) distanceSeriesRef.current.shift();
        }
      }
      if (prevIsFocusedRef.current !== focusedNow) {
        prevIsFocusedRef.current = focusedNow;
        setIsFocused(focusedNow);
      }

      if ((window as any).__debugMonitor) {
          console.log('Metrics Debug:', { 
             frozen, 
             focusedNow, 
             dt: nowMs - lastLoopMsRef.current,
             totalFocusMs: totalFocusMsRef.current,
             isRestored,
             lastSave: lastLocalSaveRef.current,
             now: nowMs
          });
      }

      if (!frozen) {
        let dt = Math.max(0, nowMs - lastLoopMsRef.current);
        // Cap dt to 65s (approx 1 min + buffer) to allow for browser background throttling (often 60s).
        // If the gap is < 65s, we treat it as continuous focus (assuming user was working in another tab).
        // If > 65s, it's a Partial Attendance gap.
        if (dt > 65000) dt = 0;

        if (!prevIsYawningRef.current && isYawning) {
          notFocusedMsRef.current += 10000;
        }
        prevIsYawningRef.current = isYawning;

        if (noFace || turned) {
          awayMsRef.current += dt;
        } else if (isPhoneDistraction) {
          phoneMsRef.current += dt;
        } else if (isDrowsy) {
          drowsyMsRef.current += dt;
        } else if (badPosture) {
          postureMsRef.current += dt;
        } else if (isYawning) {
        } else if (!focusedNow) {
          notFocusedMsRef.current += dt;
        }
        if (focusedNow) {
        sustainedFocusMsRef.current += dt;
        totalFocusMsRef.current += dt;
        } else {
        sustainedFocusMsRef.current = 0;
        lastFocusStartRef.current = nowMs;
        }

        if (poorQuality) poorQualityMsRef.current += dt;
        if (tooFar) tooFarMsRef.current += dt;
        if (orientFail) orientFailMsRef.current += dt;
        if (lookDown) lookDownMsRef.current += dt;
        
        lastLoopMsRef.current = nowMs;

        // Time sync validation: Check if local clock has drifted significantly
        if (Math.abs(dt - (1000 / 30)) > 500) {
           // Large frame variance detected - acceptable during startup or lag, but worth noting for precision
           // We rely on clamped dt for metrics to maintain integrity.
        }

        if (nowMs - lastHistoryUpdateRef.current >= 1000) {
          lastHistoryUpdateRef.current = nowMs;
          let state = 'focused';
          let details = '';
          if (!focusedNow) {
            state = 'distracted';
            if (isPhoneDistraction) details = 'phone';
            else if (isYawning) details = 'yawn';
            else if (noFace || turned) details = 'away';
            else if (isDrowsy) details = 'drowsy';
            else if (badPosture) details = 'posture';
            else details = 'not_focused';
          }
          focusHistoryRef.current.push({
            timestamp: nowMs,
            isFocused: focusedNow,
            state,
            details
          });
        }
      }

      const cfgRaw = localStorage.getItem('focusScoreConfig:v1');
      const cfg = cfgRaw ? JSON.parse(cfgRaw) : {};
      const weights = { ...defaultWeights, ...(cfg || {}) } as any;
      let startMs = (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined;
      if (!startMs && sessionId) {
         try {
           const t = JSON.parse(localStorage.getItem(`sessionTimes:${sessionId}`) || '{}');
           if (t.startMs) startMs = t.startMs;
         } catch {}
      }
      const endRaw = (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined;
      const endMs = (startMs && endRaw) ? computeGraceEndMs(startMs, endRaw) : undefined;
      const totalSessionMs = (startMs && endMs) ? Math.max(0, endMs - startMs) : undefined;
      const elapsedMs = startMs ? Math.max(0, nowMs - startMs) : undefined;

      if (nowMs - (lastLocalSaveRef.current || 0) >= 1000) {
        lastLocalSaveRef.current = nowMs;
        if (typeof window !== 'undefined' && (window as any).__debugMonitor) {
            console.log('Saving attempt', { sessionId, isRestored, startMs: !!startMs });
        }
        if (sessionId && isRestored && startMs) {
           const metrics = {
              phoneMs: phoneMsRef.current + (externalPhonePenaltyMsRef.current || 0),
              awayMs: awayMsRef.current,
              tabAwayMs: tabAwayMsRef.current,
              closedTotalMs: closedTotalMsRef.current,
              // Legacy support for server
              hiddenTotalMs: closedTotalMsRef.current,
              notFocusedMs: notFocusedMsRef.current,
              drowsyMs: drowsyMsRef.current,
              postureMs: postureMsRef.current,
              yawnPenaltyMs: yawnPenaltyMsRef.current,
              sustainedFocusMs: sustainedFocusMsRef.current,
              totalFocusMs: totalFocusMsRef.current,
              elapsedMs: elapsedMs || 0,
           };
           const data = {
              ts: nowMs,
              metrics,
              history: focusHistoryRef.current
           };
           try {
              localStorage.setItem(`sessionMonitor:${sessionId}`, JSON.stringify(data));
           } catch {}
           
           // Legacy support: sessionStats removed to prevent partial Smart Sync overwrites
           // The SyncQueue now handles the final authoritative upload.
        }
      }

      const activeElapsedMs = (elapsedMs || 0);
      const activeTotalMs = (totalSessionMs || 0);
      const { score, penalties, rewards, breakdown } = computeFocusScore({
        phoneMs: phoneMsRef.current + (externalPhonePenaltyMsRef.current || 0),
        awayMs: awayMsRef.current,
        tabAwayMs: tabAwayMsRef.current,
        hiddenTotalMs: closedTotalMsRef.current,
        notFocusedMs: notFocusedMsRef.current,
        cameraOffMs: cameraOffMsRef.current,
        drowsyMs: drowsyMsRef.current,
        postureMs: postureMsRef.current,
        yawnPenaltyMs: yawnPenaltyMsRef.current,
        sustainedFocusMs: sustainedFocusMsRef.current,
        totalFocusMs: totalFocusMsRef.current,
        elapsedMs: Math.max(0, activeElapsedMs),
        totalSessionMs: Math.max(0, activeTotalMs),
      }, weights);
      
      lastScoreRef.current = score;

      const canUpdateDebug = !((typeof window !== 'undefined' && (window as any).__freezeMetrics)) && (nowMs - (lastDebugUpdateRef.current || 0) > 250);
      if (canUpdateDebug) {
        lastDebugUpdateRef.current = nowMs;
        setDebug(prev => ({
          face, quality, orient,
          yaw, pitch, roll,
          eye, ear, mar,
          bboxW, bboxH,
          sinceFace, sinceOrient, vw, vh, ready,
          delegate: delegateRef.current,
          isDrowsy, isYawning, badPosture,
          detectedObjects: detectedObjects.length > 0 ? detectedObjects : prev.detectedObjects, // Keep last known objects if skipped frame
          isPhoneDistraction,
          poorQuality, tooFar, orientFail, lookDown,
          contrast: Math.round(contrastEmaRef.current),
          fps: Math.round(fpsEmaRef.current),
          focusConfidence: focusConfidenceVal,
          focusScore: score,
          focusPenalties: typeof penalties === 'number' ? Math.round(penalties) : undefined,
          focusRewards: typeof rewards === 'number' ? Math.round(rewards) : undefined,
          penaltyBreakdown: breakdown
        }));
      }
      const cycleEndPerf = performance.now();
      try {
        const durationMs = Math.max(0, cycleEndPerf - cycleStartPerf);
        perfMonRef.current?.recordCycle({
          intervalMs: Math.max(1, nowMs - (lastLoopMsRef.current || nowMs)),
          durationMs,
          score: score,
          penalties: typeof penalties === 'number' ? penalties : undefined,
          hiddenMs: closedTotalMsRef.current,
        });
      } catch {}
    };

    // --- Adaptive Frame Rate Logic ---
    // Instead of a fixed setInterval(1000), we now switch strategies based on visibility.
    // - Visible: requestAnimationFrame (~30FPS) for smooth feedback
    // - Hidden: setInterval(1000) (1FPS) to save resources
    
    // We use a "lastTick" timestamp to calculate deltas so scoring is time-based, not frame-based.
    // This ensures 1s of focus is worth 1s regardless of 1FPS or 30FPS.
    
    // Core detection wrapper that handles delta time
    const tick = () => {
      if (stopped) return;
      
      const now = Date.now();
      const deltaMs = now - lastTickRef.current;
      
      // Safety cap: If delta is huge (e.g. computer sleep), cap it to avoid massive spikes
      // But we still want to credit time if it's reasonable.
      // Let's cap at 30000ms per tick to be safe against deep sleep quirks (matching FocusTimeline gap logic).
      const safeDeltaMs = Math.min(deltaMs, 30000);
      
      if (safeDeltaMs > 0) {
        // Run detection with the actual time passed
        // Note: runDetection() logic needs to be aware it might run more often than 1s
        // Current runDetection logic seems state-based, which is fine.
        // We just need to ensure metrics accumulation uses this delta if it relies on "per call" assumptions.
        // Looking at the code, metrics like totalFocusMsRef are accumulated in runDetection?
        // Let's check runDetection implementation details.
        runDetection(); 
      }
      
      lastTickRef.current = now;
    };
    
    // Expose tick for PiP worker
    tickRef.current = tick;

    const startForegroundLoop = () => {
       if ((window as any).__debugMonitor) console.log('Starting foreground loop');
       if (intervalRef.current) {
         clearInterval(intervalRef.current);
         intervalRef.current = null;
       }
       if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
       
       const loop = () => {
         if (stopped) return;
         tick();
         rafIdRef.current = requestAnimationFrame(loop);
       };
       rafIdRef.current = requestAnimationFrame(loop);
    };

    const startBackgroundLoop = () => {
      if ((window as any).__debugMonitor) console.log('Starting background loop');
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
      
      // 1 FPS for background
      intervalRef.current = window.setInterval(() => {
        if (stopped) return;
        tick();
      }, 1000);
    };

    // Visibility Listener
    const handleVisibilityChange = () => {
      if ((window as any).__debugMonitor) console.log('Visibility changed. Hidden:', document.hidden);
      if (document.hidden) {
        startBackgroundLoop();
      } else {
        startForegroundLoop();
      }
    };

    // Init
    lastTickRef.current = Date.now();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Start initial loop based on current state
    if (document.hidden) {
      startBackgroundLoop();
    } else {
      startForegroundLoop();
    }

    const setup = async () => {
      setWorkflowStage('init');
      try {
        try {
          const sidLocal = (typeof window !== 'undefined' && (window as any).__currentSessionId) || sessionId;
          const heavyCpu = localStorage.getItem('focusPerfTestHeavyCpu') === '1';
          perfMonRef.current = initPerfMonitor({
            sid: sidLocal,
            targetFps: 1,
            enableHeavyCpu: heavyCpu,
            heavyCpuIntensity: 0.8,
            enableGcObserver: true,
            enableLongTaskObserver: true,
            memorySamplingMs: 10000,
            exportPeriodMs: 15000,
          });
          const flag = localStorage.getItem('focusPerfTest:v1');
          if (flag === '1') perfMonRef.current?.start();
        } catch {}
        const isMob = !!isMobileRef.current;
        const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '').toLowerCase() : '';
        const isIOS = /iphone|ipad|ipod/.test(ua);
        const isFacingSel = selectedDeviceId && selectedDeviceId.startsWith('facing:');
        const facing = isFacingSel ? selectedDeviceId.split(':')[1] : 'user';
        // Attempt zoom on mobile to improve tracking of small faces
        const advConstraints = isMob ? [{ zoom: 1.5 } as any] : undefined;
        const vBase = (isIOS || isFacingSel || !selectedDeviceId)
          ? { facingMode: facing, width: { ideal: isMob ? 640 : 1280 }, height: { ideal: isMob ? 480 : 720 }, frameRate: isMob ? { ideal: 30, min: 24 } : { ideal: 30 }, advanced: advConstraints }
          : { deviceId: { exact: selectedDeviceId }, width: { ideal: isMob ? 640 : 1280 }, height: { ideal: isMob ? 480 : 720 }, frameRate: isMob ? { ideal: 30, min: 24 } : { ideal: 30 }, advanced: advConstraints };
        const constraints: MediaStreamConstraints = { video: vBase, audio: false };
        try {
          const hasExternalControl = typeof inputMediaStream !== 'undefined';
          if (hasExternalControl) {
            if (inputMediaStream && inputMediaStream.active) {
              cameraOffModeRef.current = false;
              stream = inputMediaStream;
              if ((window as any).__debugMonitor) console.log('Using inputMediaStream from props');
            } else {
              cameraOffModeRef.current = true;
              try {
                const vStop = videoRef.current;
                const current = vStop?.srcObject as MediaStream | null;
                if (current) current.getTracks().forEach(t => t.stop());
                if (vStop) {
                  vStop.pause?.();
                  vStop.srcObject = null;
                }
              } catch {}
              setWorkflowStage('ready');
              // Skip hardware init; synthetic detection will handle history updates
              return;
            }
          } else {
            cameraOffModeRef.current = false;
            stream = await navigator.mediaDevices.getUserMedia(constraints);
          }
        } catch (err: any) {
          if ((err.name === 'OverconstrainedError' || err.name === 'ConstraintNotSatisfiedError')) {
             if (selectedDeviceId) {
                console.warn('Camera constraints failed (invalid device ID?), resetting to default.', err);
                setSelectedDeviceId('');
                return; // Trigger re-render with default device
             } else {
                console.warn('Camera constraints failed even with default settings, trying minimal fallback.', err);
                try {
                   stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                } catch (err2) {
                   throw err; // Throw original error if fallback also fails
                }
             }
          } else {
             throw err;
          }
        }
        try {
          const track = stream.getVideoTracks()[0];
          const caps = (track && (track as any).getCapabilities) ? (track as any).getCapabilities() : {};
          const settings = (track && track.getSettings) ? track.getSettings() : {} as any;
          (window as any).__cameraCaps = caps;
          (window as any).__cameraSettings = settings;
        } catch {}

        const v = videoRef.current;
        if (!v || stopped) return;

        v.setAttribute('playsinline', 'true');
        v.srcObject = stream;

        // Don't await metadata here, just start playing
        await v.play().catch(e => {
          console.error("Play failed", e);
          setErrorMsg('Tap Start Camera to begin');
          throw e; // Stop initialization
        });
        
        setWorkflowStage('camera');

        // DEBUG LOG
        if (typeof window !== 'undefined' && (window as any).__debugMonitor) {
            console.log('Setup: Importing vision');
        }

        const vision = await (await import('@mediapipe/tasks-vision')).FilesetResolver.forVisionTasks(wasmBase);
        const FaceLM = (await import('@mediapipe/tasks-vision')).FaceLandmarker;
        const ObjDet = (await import('@mediapipe/tasks-vision')).ObjectDetector;

        if (stopped) return;

        try {
          const options = {
            baseOptions: { delegate: 'GPU' },
            runningMode: 'VIDEO',
          } as any;
          
          if (typeof window !== 'undefined' && (window as any).__debugMonitor) {
              console.log('Setup: Creating landmarker GPU');
          }

          landmarkerRef.current = await FaceLM.createFromOptions(vision, {
            ...options,
            baseOptions: { modelAssetPath: modelUrl, delegate: 'GPU' },
            outputFaceBlendshapes: false,
            numFaces: 1,
          });
          
          if (typeof window !== 'undefined' && (window as any).__debugMonitor) {
              console.log('Setup: Created landmarker GPU', !!landmarkerRef.current);
          }

          objectDetectorRef.current = await ObjDet.createFromOptions(vision, {
            baseOptions: { modelAssetPath: objectModelUrl, delegate: 'GPU' },
            scoreThreshold: 0.3,
            runningMode: 'VIDEO',
          });
          
          if (typeof window !== 'undefined' && (window as any).__debugMonitor) {
              console.log('Setup: Created objectDetector GPU', !!objectDetectorRef.current);
          }

          delegateRef.current = 'GPU';
        } catch (e) {
          if (typeof window !== 'undefined' && (window as any).__debugMonitor) {
              console.log('Setup: GPU failed, trying CPU', e);
          }
          const options = {
            baseOptions: { delegate: 'CPU' },
            runningMode: 'VIDEO',
          } as any;

          landmarkerRef.current = await FaceLM.createFromOptions(vision, {
            ...options,
            baseOptions: { modelAssetPath: modelUrl, delegate: 'CPU' },
            outputFaceBlendshapes: false,
            numFaces: 1,
          });

          objectDetectorRef.current = await ObjDet.createFromOptions(vision, {
            baseOptions: { modelAssetPath: objectModelUrl, delegate: 'CPU' },
            scoreThreshold: 0.3,
            runningMode: 'VIDEO',
          });

          delegateRef.current = 'CPU';
        }
        
        setWorkflowStage('ready');
        // Initialize timestamps to prevent immediate "Away" scoring
        const nowReady = Date.now();
        lastFaceTsRef.current = nowReady;
        lastFacingTsRef.current = nowReady;
        drowsyStartTsRef.current = 0;
        yawnStartTsRef.current = 0;
        postureStartTsRef.current = 0;
        qualityFailStartTsRef.current = 0;
        orientFailStartTsRef.current = 0;
        distanceFailStartTsRef.current = 0;
        lookDownStartTsRef.current = 0;
      } catch (e: any) {
        console.error(e);
        let msg = e?.message || String(e);
        if (msg === '[object Event]' && e.type) msg = `Event: ${e.type}`;
        
        if (e.name === 'AbortError' || msg.includes('aborted')) {
             setErrorMsg('Camera access stopped. Please restart session or refresh.');
        } else if (e.name === 'NotAllowedError' || msg.includes('Permission denied')) {
             setErrorMsg('Camera permission denied. Please allow access.');
        } else if (stream && stream.active) {
             let displayMsg = 'Focus tracking initialization failed. Focus features unavailable.';
             if (msg.includes('Event') || msg.includes('Network') || msg.includes('fetch') || msg.includes('Failed to fetch')) {
                 displayMsg += ' (Check internet connection or firewall)';
             } else {
                 displayMsg += ' (' + msg + ')';
             }
             setErrorMsg(displayMsg);
         } else {
            setErrorMsg('Camera access failed: ' + msg);
        }
      }
    };

    const dist = (p1: NormalizedLandmark, p2: NormalizedLandmark) => {
      const dx = p1.x - p2.x;
      const dy = p1.y - p2.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const calculateEAR = (landmarks: NormalizedLandmark[]) => {
      // Left Eye: 33, 160, 158, 133, 153, 144
      const l1 = dist(landmarks[160], landmarks[144]);
      const l2 = dist(landmarks[158], landmarks[153]);
      const l3 = dist(landmarks[33], landmarks[133]);
      const leftEAR = (l1 + l2) / (2.0 * l3);

      // Right Eye: 362, 385, 387, 263, 373, 380
      const r1 = dist(landmarks[385], landmarks[380]);
      const r2 = dist(landmarks[387], landmarks[373]);
      const r3 = dist(landmarks[362], landmarks[263]);
      const rightEAR = (r1 + r2) / (2.0 * r3);

      return (leftEAR + rightEAR) / 2.0;
    };

    const calculateMAR = (landmarks: NormalizedLandmark[]) => {
      // Mouth: 13 (upper), 14 (lower), 61 (left), 291 (right)
      const h = dist(landmarks[13], landmarks[14]);
      const w = dist(landmarks[61], landmarks[291]);
      return h / w;
    };

    const measureOrientation = (landmarks: NormalizedLandmark[] | null) => {
      if (!landmarks || landmarks.length < 300) return { ok: false, yaw: 0, pitch: 0, roll: 0 };
      const nose = landmarks[1];
      const leftEye = landmarks[33];
      const rightEye = landmarks[263];
      if (!nose || !leftEye || !rightEye) return { ok: false, yaw: 0, pitch: 0, roll: 0 };

      const yawVal = (rightEye.x - nose.x) - (nose.x - leftEye.x);
      const pitchVal = nose.y - ((leftEye.y + rightEye.y) / 2);

      // Roll: Angle between eyes
      const dy = rightEye.y - leftEye.y;
      const dx = rightEye.x - leftEye.x;
      const rollVal = Math.atan2(dy, dx);

      const eyeDist = Math.sqrt(dx*dx + dy*dy);
      const safeEyeDist = Math.max(0.01, eyeDist);

      const yawLimit = safeEyeDist * ORIENT_YAW_RATIO;
      const pitchLimit = safeEyeDist * ORIENT_PITCH_RATIO;

      const yawOk = Math.abs(yawVal) < yawLimit;
      const pitchOk = Math.abs(pitchVal) < pitchLimit;
      // Roll check is done separately in the loop for timing

      return { ok: yawOk && pitchOk, yaw: yawVal, pitch: pitchVal, roll: rollVal };
    };

    const measureFaceQuality = (landmarks: NormalizedLandmark[] | null) => {
      if (!landmarks || landmarks.length < 300) return { ok: false, eye: 0, bboxW: 0, bboxH: 0 };
      const leftEye = landmarks[33];
      const rightEye = landmarks[263];
      if (!leftEye || !rightEye) return { ok: false, eye: 0, bboxW: 0, bboxH: 0 };
      const dx = Math.abs(rightEye.x - leftEye.x);
      const dy = Math.abs(rightEye.y - leftEye.y);
      const eyeDist = Math.sqrt(dx * dx + dy * dy);
      let minX = 1, maxX = 0, minY = 1, maxY = 0;
      for (let i = 0; i < landmarks.length; i++) {
        const p = landmarks[i];
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
      const bboxW = Math.max(0, maxX - minX);
      const bboxH = Math.max(0, maxY - minY);
      const boxOk = bboxW >= MIN_BBOX_W && bboxH >= MIN_BBOX_H;
      const ok = eyeDist >= MIN_EYE_DIST && boxOk;
      return { ok, eye: eyeDist, bboxW, bboxH };
    };

    const draw = (faceLandmarks: NormalizedLandmark[] | null) => {
      const v = videoRef.current;
      const c = canvasRef.current;
      if (!v || !c) return;
      const w = v.videoWidth || 640;
      const h = v.videoHeight || 480;
      if (c.width !== w) c.width = w;
      if (c.height !== h) c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(v, 0, 0, w, h);

      if (faceLandmarks && faceLandmarks.length) {
        ctx.strokeStyle = '#EBA832'; // Honey Yellow
        ctx.lineWidth = 1;
        
        // Calculate bounding box for PiP zooming
        let minX = 1, maxX = 0, minY = 1, maxY = 0;
        faceLandmarks.forEach((p) => {
          if (p.x < minX) minX = p.x;
          if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
          const x = p.x * w;
          const y = p.y * h;
          ctx.beginPath();
          ctx.arc(x, y, 1.5, 0, Math.PI * 2);
          ctx.stroke();
        });
        
        // Update Face Box Ref for PiP (smoothed)
        const cx = (minX + maxX) / 2;
        const cy = (minY + maxY) / 2;
        const fw = maxX - minX;
        const fh = maxY - minY;
        
        if (!pipFaceBoxRef.current) {
          pipFaceBoxRef.current = { cx, cy, w: fw, h: fh };
        } else {
          // Smooth transition (lerp)
          const curr = pipFaceBoxRef.current;
          const alpha = 0.1;
          curr.cx = curr.cx * (1 - alpha) + cx * alpha;
          curr.cy = curr.cy * (1 - alpha) + cy * alpha;
          curr.w = curr.w * (1 - alpha) + fw * alpha;
          curr.h = curr.h * (1 - alpha) + fh * alpha;
        }
      }
    };

    if (started) setup();
    const videoEl = videoRef.current;

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      
      try { perfMonRef.current?.stop?.(); } catch {}
      if (videoEl) {
        try { videoEl.pause(); } catch { }
        videoEl.srcObject = null;
      }
      const lm = landmarkerRef.current;
      try { (lm as any)?.close?.(); } catch { }
      landmarkerRef.current = null;

      const oDet = objectDetectorRef.current;
      try { (oDet as any)?.close?.(); } catch { }
      objectDetectorRef.current = null;

      if (stream && stream !== inputMediaStream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, [started, selectedDeviceId, sessionId, isRestored, inputMediaStream]);
  useEffect(() => {
    // Pre-warm PiP video when camera starts to avoid user activation errors later
    if (!started) return;
    const v = videoRef.current;
    const pipCanvas = pipCanvasRef.current;
    const pipVideo = pipVideoRef.current;
    if (!v || !pipCanvas || !pipVideo) return;

    let stopped = false;
    const initPiP = async () => {
      // Wait for video to be ready
      if (v.readyState < 2) {
        await new Promise<void>(resolve => {
          const onReady = () => {
             v.removeEventListener('canplay', onReady);
             resolve();
          };
          v.addEventListener('canplay', onReady);
        });
      }
      if (stopped) return;

      const baseW = v.videoWidth || 640;
      const baseH = v.videoHeight || 480;
      const size = Math.min(baseW, baseH);
      if (pipCanvas.width !== size) pipCanvas.width = size;
      if (pipCanvas.height !== size) pipCanvas.height = size;

      // Start stream
      const stream = (pipCanvas as any).captureStream ? (pipCanvas as any).captureStream(30) : null;
      if (stream) {
        pipVideo.srcObject = stream;
        pipVideo.muted = true;
        try { await pipVideo.play(); } catch {}
      }
    };
    initPiP();

    return () => { stopped = true; };
  }, [started]);



  const toggleComposedPiP = useCallback(async (silent: boolean = false) => {
    const v = videoRef.current as HTMLVideoElement | null;
    if (!v || v.readyState < 2) { if (!silent) setPipMsg('Camera not ready'); return; }
    
    // Check if we need to close existing PiP (if active or if browser thinks it is active)
    if (document.pictureInPictureElement) {
        try {
            await document.exitPictureInPicture();
        } catch(e) { console.error("Exit PiP error", e); }
        setPipComposedActive(false);
        return;
    }

    const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
    const isSafari = !!ua && (/safari/.test(ua) && !/chrome|crios|android/.test(ua));
    if (!pipComposedActive) {
      try {
        const pipCanvas = pipCanvasRef.current;
        const pipVideo = pipVideoRef.current;
        if (!pipCanvas || !pipVideo) { if (!silent) setPipMsg('PiP surface not ready'); return; }

        const baseW = v.videoWidth || 640;
        const baseH = v.videoHeight || 480;
        const size = Math.min(baseW, baseH);
        if (pipCanvas.width !== size) pipCanvas.width = size;
        if (pipCanvas.height !== size) pipCanvas.height = size;
        const ctx = pipCanvas.getContext('2d');
        if (!ctx) { if (!silent) setPipMsg('Canvas unsupported'); return; }

        // Initialize Background Worker
        if (!pipWorkerRef.current) {
          pipWorkerRef.current = new Worker('/pip-worker.js');
        }

        pipDrawActiveRef.current = true;
        
        // Main Draw Function
        const draw = () => {
          if (!pipDrawActiveRef.current) return;
          
          // Ensure source video is playing if paused in background
          if (v.paused) v.play().catch(() => {});

          // Determine Target Dimensions (Square for better PiP fit)
          const size = Math.min(v.videoWidth || baseW, v.videoHeight || baseH);
          const cw = size;
          const ch = size;
          
          if (pipCanvas.width !== cw) pipCanvas.width = cw;
          if (pipCanvas.height !== ch) pipCanvas.height = ch;
          
          ctx.clearRect(0, 0, cw, ch);
          
          // Dynamic Filter based on Focus State
          // Unfocused -> Yellow Tint
          // Focused -> Standard Grayscale (Dimmed)
          const isFoc = isFocusedRef.current;
          if (isFoc) {
             ctx.filter = 'grayscale(100%) brightness(0.35)';
          } else {
             // Yellow warning tint
             ctx.filter = 'sepia(100%) saturate(300%) hue-rotate(10deg) brightness(0.6) contrast(1.1)';
          }
          
          // Smart Zoom Logic
          let sx = 0, sy = 0, sw = v.videoWidth || baseW, sh = v.videoHeight || baseH;
          
          if (pipFaceBoxRef.current) {
            // Zoom in on face
            const { cx } = pipFaceBoxRef.current;
            
            // Logic: Take a square crop of size 'sh' (full height) from the video
            // Use Math.min to handle portrait mode or unexpected dimensions
            const cropSize = Math.min(sw, sh);
            const halfCrop = cropSize / 2;
            
            // Center X on face (convert normalized cx to pixels), but clamp
            let centerX = cx * sw;
            
            // If face is too far left, clamp
            if (centerX < halfCrop) centerX = halfCrop;
            // If face is too far right, clamp
            if (centerX > sw - halfCrop) centerX = sw - halfCrop;
            
            sx = centerX - halfCrop;
            sy = (sh - cropSize) / 2; // Center Y as well
            sw = cropSize;
            sh = cropSize;
          } else {
            // Default center crop
            const cropSize = Math.min(sw, sh);
            sx = (sw - cropSize) / 2;
            sy = (sh - cropSize) / 2;
            sw = cropSize;
            sh = cropSize;
          }

          // Mirror the video draw
          ctx.save();
          ctx.translate(cw, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(v, sx, sy, sw, sh, 0, 0, cw, ch);
          ctx.restore();
          
          ctx.filter = 'none';

          // Ensure video is greyed out even if filter fails (fallback)
          ctx.globalCompositeOperation = 'saturation';
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, cw, ch);
          ctx.globalCompositeOperation = 'source-over';

          // Overlay Metrics
          const smCur = smRef.current || { distractions: [], sessionSeconds: 0, percentFocused: 0 };
          const foc = isFocusedRef.current;
          
          // Use the live computed score from runDetection for perfect sync
          // Fallback to smCur if unavailable
          let goalPct = typeof lastScoreRef.current === 'number' ? lastScoreRef.current : (typeof smCur.percentFocused === 'number' ? smCur.percentFocused : 0);
          
          try {
             // Redundant local storage check removed in favor of live ref
          } catch {}

          const startMs = (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined;
          const endRaw = (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined;
          const endMs = (startMs && endRaw) ? computeGraceEndMs(startMs, endRaw) : undefined;
          const nowTs = Date.now();
          const showCountdown = !!(startMs && nowTs < startMs);
          const targetMs = showCountdown ? startMs : endMs;
          const rem = (targetMs ? Math.max(0, Math.floor((targetMs - nowTs) / 1000)) : 0);
          const hh = Math.floor(rem / 3600);
          const mm = String(Math.floor((rem % 3600) / 60)).padStart(2, '0');
          const ss = String(rem % 60).padStart(2, '0');
          const clockStr = hh > 0 ? `${String(hh).padStart(2,'0')}:${mm}:${ss}` : `${mm}:${ss}`;


          // New Layout: Donut Center, Timer Larger
          const areaW = cw * 0.9;
          const areaH = ch * 0.6;
          const cx = cw / 2;
          const cy = ch / 2;
          
          // Donut Settings - Centered
          const donutRadius = Math.min(areaW * 0.25, areaH * 0.45);
          const cxDonut = cx;
          const cyDonut = cy;
          const thickness = donutRadius * 0.15;
          const fontSize = donutRadius * 0.5;
          
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          // --- Draw Donut ---
          ctx.lineWidth = thickness;
          ctx.strokeStyle = 'rgba(255,255,255,0.2)';
          ctx.beginPath();
          ctx.arc(cxDonut, cyDonut, donutRadius, 0, Math.PI * 2);
          ctx.stroke();

          const startAng = -Math.PI / 2;
          const pctVal = Math.max(0, Math.min(100, goalPct));
          const endAng = startAng + (pctVal / 100) * Math.PI * 2;
          
          ctx.strokeStyle = foc ? '#4caf50' : '#f44336';
          ctx.lineWidth = thickness + 2;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.arc(cxDonut, cyDonut, donutRadius, startAng, endAng);
          ctx.stroke();

          // Text inside donut
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `bold ${Math.floor(fontSize * 1.5)}px sans-serif`;
          ctx.fillStyle = '#fff';
          ctx.fillText(`${Math.round(pctVal)}%`, cxDonut, cyDonut);

          // --- Draw Stats ---
          const labelFontSize = fontSize * 0.8;
          ctx.font = `bold ${Math.floor(labelFontSize)}px sans-serif`;
          
          // 1. Focused Status (Top Middle)
          const topY = ch * 0.1; 
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          
          const stateText = focusStateRef.current;
          const isFocused = stateText === 'Focused';
          
          ctx.fillStyle = isFocused ? '#4caf50' : '#f44336';
          ctx.fillText(stateText, cw / 2, topY);

          // 2. Timer (Bottom Middle) - Larger
          // Move lower to avoid overlapping with donut
          const bottomY = ch * 0.96; 
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          // Increase font size significantly (e.g., 1.5x larger than before)
          const timerFontSize = labelFontSize * 1.5;
          ctx.font = `bold ${Math.floor(timerFontSize)}px sans-serif`;
          ctx.fillStyle = '#fff';
          ctx.fillText(clockStr, cw / 2, bottomY);
        };

        // Visibility Handler
        const handleVisibility = () => {
          if (!pipDrawActiveRef.current) return;
          if (document.hidden) {
             if (pipDrawAnimRef.current) cancelAnimationFrame(pipDrawAnimRef.current);
             pipWorkerRef.current?.postMessage('start');
          } else {
             pipWorkerRef.current?.postMessage('stop');
             const loop = () => {
               if (!pipDrawActiveRef.current) return;
               draw();
               pipDrawAnimRef.current = requestAnimationFrame(loop);
             };
             loop();
          }
        };

        // Worker Listener
        if (pipWorkerRef.current) {
          pipWorkerRef.current.onmessage = () => {
            if (document.hidden && pipDrawActiveRef.current) {
               // Boost FPS for detection if PiP is active in background
               if (tickRef.current) tickRef.current();
               draw();
            }
          };
        }

        document.addEventListener('visibilitychange', handleVisibility);
        
        // Initial start
        handleVisibility();

        // Ensure PiP video is playing before request
        if (!pipVideo.srcObject) {
           const stream = (pipCanvas as any).captureStream ? (pipCanvas as any).captureStream(30) : null;
           if (!stream) { if (!silent) setPipMsg('PiP capture not supported'); return; }
           pipVideo.srcObject = stream as any;
           pipVideo.muted = true;
        }


        if (pipVideo.paused) {
           pipVideo.play().catch((e: any) => console.error("PiP Play Error", e));
        }

        // Wait for video metadata to be loaded (crucial for Safari/Chrome PiP)
        if (pipVideo.readyState < 1) {
            await new Promise<void>((resolve) => {
                const onMeta = () => {
                    pipVideo.removeEventListener('loadedmetadata', onMeta);
                    resolve();
                };
                pipVideo.addEventListener('loadedmetadata', onMeta);
                // Fallback if event never fires (e.g. already loaded race condition)
                if (pipVideo.readyState >= 1) {
                    pipVideo.removeEventListener('loadedmetadata', onMeta);
                    resolve();
                } else {
                    // Reduce timeout to 500ms to preserve user activation token
                    setTimeout(resolve, 500); 
                }
            });
        }

        if (typeof (pipVideo as any).requestPictureInPicture === 'function') {
          await (pipVideo as any).requestPictureInPicture();
          setPipComposedActive(true);
        } else if (isSafari && typeof (pipVideo as any).webkitSetPresentationMode === 'function') {
          (pipVideo as any).webkitSetPresentationMode('picture-in-picture');
          setPipComposedActive(true);
        } else {
          if (!silent) setPipMsg('Video PiP not supported');
        }

        const onLeavePiP = async () => {
          pipDrawActiveRef.current = false;
          document.removeEventListener('visibilitychange', handleVisibility);
          pipWorkerRef.current?.postMessage('stop');
          if (pipDrawAnimRef.current) cancelAnimationFrame(pipDrawAnimRef.current);
          setPipComposedActive(false);
          pipVideo.removeEventListener('leavepictureinpicture', onLeavePiP);
          try { if (document.pictureInPictureElement) await (document as any).exitPictureInPicture(); } catch {}
        };
        pipVideo.addEventListener('leavepictureinpicture', onLeavePiP);
      } catch (err: any) {
        const msg = err && err.message ? String(err.message) : String(err);
        if (!silent) setPipMsg('Video PiP failed: ' + msg);
        pipDrawActiveRef.current = false;
        console.error("PiP Start Error:", err);
      }
      return;
    }
    // Close PiP
    try {
      const pipVideo = pipVideoRef.current as any;
      if (isSafari && v && (v as any).webkitPresentationMode === 'picture-in-picture' && typeof (v as any).webkitSetPresentationMode === 'function') {
        (v as any).webkitSetPresentationMode('inline');
      }
      try { if (document.pictureInPictureElement) await (document as any).exitPictureInPicture(); } catch {}
      pipDrawActiveRef.current = false;
      pipWorkerRef.current?.postMessage('stop'); // Stop worker
      if (pipDrawAnimRef.current) cancelAnimationFrame(pipDrawAnimRef.current);
      try { if (pipVideo) { pipVideo.pause(); } } catch {}
    } catch {}
    setPipComposedActive(false);
  }, [pipComposedActive]);
  const sm = useSessionManager({
    started,
    isFocused,
    face: debug.face,
    orient: debug.orient,
    targetWindow: window,
    graceMs: 10000,
    sessionStartMs: (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined,
    sessionEndMs: (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined,
    getFocusWeight,
    freeze: !!((typeof window !== 'undefined' && (window as any).__freezeMetrics)),
    onBreak,
  } as any);
  useEffect(() => { smRef.current = sm; }, [sm]);
  useEffect(() => { try { (window as any).__sessionMetrics = sm; } catch {} }, [sm]);
  useEffect(() => { isFocusedRef.current = isFocused; }, [isFocused]);
  useEffect(() => { debugRef.current = debug; }, [debug]);

  useEffect(() => {
    const c = mirrorCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const drawMirror = () => {
      const smCur = smRef.current || { distractions: [], sessionSeconds: 0, percentFocused: 0 };
      const foc = isFocusedRef.current;
      let goalPct = typeof smCur.percentFocused === 'number' ? smCur.percentFocused : 0;
      try {
        const sidLocal = (typeof window !== 'undefined' && (window as any).__currentSessionId) || undefined;
        if (sidLocal) {
          const raw = localStorage.getItem(`sessionStats:${sidLocal}`) || '{}';
          const stats = JSON.parse(raw);
          if (typeof stats?.focusScore === 'number') goalPct = stats.focusScore;
        }
      } catch {}
      const startMs = (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined;
      const endRaw = (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined;
      const endMs = (startMs && endRaw) ? computeGraceEndMs(startMs, endRaw) : undefined;
      const nowTs = Date.now();
      const showCountdown = !!(startMs && nowTs < startMs);
      const targetMs = showCountdown ? startMs : endMs;
      const rem = (targetMs ? Math.max(0, Math.floor((targetMs - nowTs) / 1000)) : 0);
      const hh = Math.floor(rem / 3600);
      const mm = String(Math.floor((rem % 3600) / 60)).padStart(2, '0');
      const ss = String(rem % 60).padStart(2, '0');
      const clockStr = hh > 0 ? `${String(hh).padStart(2,'0')}:${mm}:${ss}` : `${mm}:${ss}`;

      ctx.clearRect(0, 0, c.width, c.height);
      ctx.fillStyle = '#FFF1B8';
      ctx.fillRect(0, 0, c.width, c.height);
      const pad = Math.floor(c.height * 0.02);
      const fontSize = Math.floor(c.height * 0.085);
      const gutter = 20;
      const radius = Math.floor(c.height * 0.22);
      const cxDonut = Math.floor(c.width / 2);
      const offsetUp = Math.floor(c.height * 0.06);
      const cyDonut = pad + gutter + radius - offsetUp;
      const thickness = Math.floor(fontSize * 0.5);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.font = `${fontSize}px Arial, Helvetica, sans-serif`;
      ctx.fillStyle = '#000';
      // Focused label relocated to bottom stack
      ctx.lineWidth = thickness;
      ctx.strokeStyle = '#e0e0e0';
      ctx.beginPath();
      ctx.arc(cxDonut, cyDonut, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = '#4caf50';
      const startAng = -Math.PI / 2;
      const pct = Math.max(0, Math.min(100, goalPct));
      const endAng = startAng + (pct / 100) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cxDonut, cyDonut, radius, startAng, endAng);
      ctx.stroke();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${Math.floor(fontSize * 1.1)}px Arial, Helvetica, sans-serif`;
      ctx.fillStyle = '#000';
      ctx.fillText(`${Math.round(pct)}%`, cxDonut, cyDonut);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.font = `600 ${Math.floor(fontSize * 0.9)}px Arial, Helvetica, sans-serif`;
      ctx.fillStyle = '#333';
      ctx.fillText('Focus Score', cxDonut, cyDonut + radius + 8);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.font = `${fontSize}px Arial, Helvetica, sans-serif`;
      const yEnds = c.height - pad;
      const yFocused = yEnds - Math.floor(fontSize * 1.05);
      const xCenter = Math.floor(c.width / 2);
      ctx.fillText(`Focused: ${foc ? 'Yes' : 'No'}`, xCenter, yFocused);
      ctx.fillText(`Ends in: ${clockStr}`, xCenter, yEnds);
      if (logoImgRef.current && logoImgRef.current.naturalWidth > 0) {
        const imgL = logoImgRef.current;
        const lSize = Math.floor(c.height * 0.10);
        const lx = pad;
        const ly = c.height - pad - lSize;
        ctx.drawImage(imgL, lx, ly, lSize, lSize);
      }
      mirrorAnimRef.current = requestAnimationFrame(drawMirror);
    };
    mirrorAnimRef.current = requestAnimationFrame(drawMirror);
    return () => { if (mirrorAnimRef.current) cancelAnimationFrame(mirrorAnimRef.current); };
  }, [sm]);



  // Freeze management
  useEffect(() => {
    const id = window.setInterval(() => {
      const now = Date.now();
      try {
        const startMs = (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined;
        const preFreeze = !!(startMs && now < startMs);
        (window as any).__freezeMetrics = preFreeze || closingRef.current;
      } catch {}
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onPageHide = () => {
      try { console.log('[WindowState] pagehide: closing session, freezing metrics'); } catch {}
      closingRef.current = true;
      const sid = (typeof window !== 'undefined' && (window as any).__currentSessionId) || undefined;
      const now = Date.now();
      hiddenStartRef.current = now;
      if (sid) {
        try { localStorage.setItem(`sessionHiddenStartMs:${sid}`, String(now)); } catch {}
        try { localStorage.setItem(`sessionClosedAt:${sid}`, String(now)); } catch {}
        try {
          const uid = Number(localStorage.getItem('userId') || '0');
          const statsRaw = localStorage.getItem(`sessionStats:${sid}`) || '{}';
          const stats = JSON.parse(statsRaw || '{}');
          if (uid) {
            const accKey = `sessionHiddenAccumMs:${sid}`;
            const hiddenAcc = Number(localStorage.getItem(accKey) || '0');
            const tabAwaySec = Math.floor((tabAwayMsRef.current || 0) / 1000);
            const payload: any = {
              userId: uid,
              percent_focused: Math.round(Math.max(0, Math.min(100, Number(stats.focusScore || 0)))),
              away_seconds: Number(stats.awaySeconds || 0),
              phone_seconds: Number(stats.phoneSeconds || 0),
              drowsy_seconds: Number(stats.drowsySeconds || 0),
              posture_seconds: Number(stats.postureSeconds || 0),
              not_focused_seconds: Number(stats.notFocusedSeconds || 0),
              hidden_seconds: 0,
              tab_away_seconds: tabAwaySec,
              summary_para1: `window_state=hidden; hiddenAccumMs=${hiddenAcc}; tabAwayMs=${tabAwayMsRef.current || 0}`,
              summary_para2: null
            };
            api.post(`/session/${sid}/metrics`, payload, { skipAuthRedirect: true } as any).catch(() => {});
          }
        } catch {}
      }
    };
    const onPageShow = () => {
      try { console.log('[WindowState] pageshow: resuming session'); } catch {}
      closingRef.current = false;
      const sid = (typeof window !== 'undefined' && (window as any).__currentSessionId) || undefined;
      const now = Date.now();
      let startMs = hiddenStartRef.current;
      if (!startMs && sid) {
        try { startMs = Number(localStorage.getItem(`sessionHiddenStartMs:${sid}`) || '0'); } catch {}
      }
      if (startMs > 0) {
        const delta = Math.max(0, now - startMs);
        closedTotalMsRef.current += delta;
        
        // FIX: Hidden time is now treated strictly as "Paused" time, not "Away" time.
        // We do NOT increment awayMsRef here.
        // We do NOT apply any immediate score penalty.
        // This ensures scores are preserved after long breaks.
        
        hiddenStartRef.current = 0;
        
        if (sid) {
          try {
            const accKey = `sessionHiddenAccumMs:${sid}`;
            const prev = Number(localStorage.getItem(accKey) || '0');
            localStorage.setItem(accKey, String(prev + delta));
            localStorage.removeItem(`sessionHiddenStartMs:${sid}`);
            const closedAtStr = localStorage.getItem(`sessionClosedAt:${sid}`) || '0';
            const closedAt = Number(closedAtStr || '0');
            if (closedAt > 0) {
              const closedDelta = Math.max(0, now - closedAt);
              const closedAccKey = `sessionClosedAccumMs:${sid}`;
              const prevClosedAcc = Number(localStorage.getItem(closedAccKey) || '0');
              localStorage.setItem(closedAccKey, String(prevClosedAcc + closedDelta));
              localStorage.removeItem(`sessionClosedAt:${sid}`);
            }
          } catch {}
        }
        try {
          const uid = Number(localStorage.getItem('userId') || '0');
          const statsRaw = sid ? (localStorage.getItem(`sessionStats:${sid}`) || '{}') : '{}';
          const stats = JSON.parse(statsRaw || '{}');
          if (uid && sid) {
            const accKey = `sessionHiddenAccumMs:${sid}`;
            const hiddenAcc = Number(localStorage.getItem(accKey) || '0');
            const tabAwaySec = Math.floor((tabAwayMsRef.current || 0) / 1000);
            const payload: any = {
              userId: uid,
              percent_focused: Math.round(Math.max(0, Math.min(100, Number(stats.focusScore || 0)))),
              away_seconds: Math.floor(Number(stats.awaySeconds || 0)),
              phone_seconds: Number(stats.phoneSeconds || 0),
              drowsy_seconds: Number(stats.drowsySeconds || 0),
              posture_seconds: Number(stats.postureSeconds || 0),
              not_focused_seconds: Number(stats.notFocusedSeconds || 0),
              hidden_seconds: Math.floor(delta / 1000),
              tab_away_seconds: tabAwaySec,
              summary_para1: `window_state=visible; hiddenDeltaMs=${delta}; hiddenAccumMs=${hiddenAcc}; tabAwayMs=${tabAwayMsRef.current || 0}`,
              summary_para2: null
            };
            api.post(`/session/${sid}/metrics`, payload, { skipAuthRedirect: true } as any).catch(() => {});
          }
        } catch {}
      }
    };
    const initAccum = () => {
      const sid = (typeof window !== 'undefined' && (window as any).__currentSessionId) || undefined;
      if (!sid) return;
      try {
        const prevAcc = Number(localStorage.getItem(`sessionHiddenAccumMs:${sid}`) || '0');
        closedTotalMsRef.current = Math.max(0, prevAcc);
        const start = Number(localStorage.getItem(`sessionHiddenStartMs:${sid}`) || '0');
        if (start > 0) {
          const now = Date.now();
          const delta = Math.max(0, now - start);
          closedTotalMsRef.current += delta;
          localStorage.setItem(`sessionHiddenAccumMs:${sid}`, String(closedTotalMsRef.current));
          localStorage.removeItem(`sessionHiddenStartMs:${sid}`);
        }
      } catch {}
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('beforeunload', onPageHide);
    initAccum();
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, []);
  useEffect(() => {
    try {
      const sid = (typeof window !== 'undefined' && (window as any).__currentSessionId) || undefined;
      const uid = Number(localStorage.getItem('userId') || '0');
      if (sid && uid) {
        api.get(`/session/${sid}/metrics`, { skipAuthRedirect: true } as any).then((resp) => {
          const rows = (resp && resp.data && Array.isArray(resp.data.metrics)) ? resp.data.metrics : [];
          const row = rows.find((r: any) => Number(r.user_id) === uid) || null;
          if (row) {
            const prevStr = localStorage.getItem(`sessionStats:${sid}`);
            const prev = prevStr ? JSON.parse(prevStr) : {};
            const out = {
              ...prev,
              focusScore: Math.round(Math.max(0, Math.min(100, Number(row.percent_focused || 0)))),
              totalFocusSeconds: Number(row.total_focus_seconds || 0),
              lastUpdated: Date.now()
            };
            localStorage.setItem(`sessionStats:${sid}`, JSON.stringify(out));
          }
        }).catch(() => {});
      }
    } catch {}
  }, []);
  useEffect(() => {
    try { (window as any).__cameraMetrics = debug; } catch {}
    try { window.dispatchEvent(new CustomEvent('camera-metrics', { detail: debug })); } catch {}
    // Sync live focus score for UI smoothness
    try { window.dispatchEvent(new CustomEvent('focus-score', { detail: (debug as any)?.focusScore })); } catch {}
    try {
      const prevV = Number(localStorage.getItem('metricsPersistVersion') || '0');
      const version = Math.max(prevV, 1);
        const snapshot = {
          version,
          ts: Date.now(),
          focusConfidence: debug.focusConfidence,
          contrast: debug.contrast,
          face: debug.face,
          orient: debug.orient,
          isPhoneDistraction: debug.isPhoneDistraction,
          focusScore: (debug as any)?.focusScore
        };
      localStorage.setItem('metricsPersist:v1', JSON.stringify(snapshot));
      localStorage.setItem('metricsPersistVersion', String(version));
    } catch {}
  }, [debug]);
  useEffect(() => {
    try { (window as any).__cameraIsFocused = isFocused; } catch {}
    try { window.dispatchEvent(new CustomEvent('camera-focused', { detail: isFocused })); } catch {}
  }, [isFocused]);
  useEffect(() => {
    try { (window as any).__cameraActive = started; } catch {}
    try {
      window.dispatchEvent(new CustomEvent('camera-active', { detail: started }));
      if (started) window.dispatchEvent(new Event('camera-started'));
    } catch {}
  }, [started]);
  useEffect(() => {
    try {
      if (started) (window as any).__cameraEverStarted = true;
    } catch {}
  }, [started]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('cameraPersist:v1');
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj.selectedDeviceId === 'string') {
          setSelectedDeviceId(obj.selectedDeviceId);
        }
        if (obj && obj.zoom) {
          (window as any).__cameraSettings = { ...(window as any).__cameraSettings, zoom: obj.zoom };
        }
        if (obj && obj.focusPoint) {
          (window as any).__cameraSettings = { ...(window as any).__cameraSettings, focusPoint: obj.focusPoint };
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const version = 1;
      const prev = localStorage.getItem('cameraPersist:v1');
      const prevObj = prev ? JSON.parse(prev) : {};
      const next = {
        ...prevObj,
        version,
        selectedDeviceId,
        zoom: (window as any).__cameraSettings?.zoom,
        focusPoint: (window as any).__cameraSettings?.focusPoint,
        updatedAt: Date.now(),
      };
      localStorage.setItem('cameraPersist:v1', JSON.stringify(next));
      localStorage.setItem('cameraPersistVersion', String(version));
    } catch {}
  }, [selectedDeviceId]);

  useEffect(() => {
    try {
      const sid = (typeof window !== 'undefined' && (window as any).__currentSessionId) || undefined;
      // DEBUG LOG
      if (!sid || !sm || !isRestored) return;
      const prevStr = localStorage.getItem(`sessionStats:${sid}`);
      const prev = prevStr ? JSON.parse(prevStr) : {};
      const prevD = Array.isArray(prev?.distractions) ? prev.distractions : [];
      const currD = Array.isArray((sm as any)?.distractions) ? (sm as any).distractions : [];
      const merged = Array.from(new Set([...(prevD as number[]), ...(currD as number[])])).sort();
      const cfgRaw = localStorage.getItem('focusScoreConfig:v1');
      const weights = { ...defaultWeights, ...((cfgRaw ? JSON.parse(cfgRaw) : {}) || {}) } as any;
      let startMs = (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined;
      if (!startMs && sid) {
           try {
             const t = JSON.parse(localStorage.getItem(`sessionTimes:${sid}`) || '{}');
             if (t.startMs) startMs = t.startMs;
           } catch {}
      }
      if (!startMs) return;
      const nowMs = Date.now();
      const endRaw = (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined;
      const endMs = (startMs && endRaw) ? computeGraceEndMs(startMs, endRaw) : undefined;
      // Calculation based on NEW Scoring Logic (Target - Penalty / Target)
      // We need to pass the Total Session Duration (Target).
      // We derive it from startMs and endMs.
      
      const totalSessionMsCalc = (startMs && endMs) ? Math.max(0, endMs - startMs) : undefined;
      const elapsedMsCalc = startMs ? Math.max(0, nowMs - startMs) : undefined;
      
      // Note: effectiveElapsedMs (active) logic is replaced by the new formula where hidden is a penalty.
      // But we still track closedTotalMsRef.current as the source of "Inactive" penalty.

      const { score, blocks, penalties, rewards, breakdown } = computeFocusScore({
        phoneMs: phoneMsRef.current,
        awayMs: awayMsRef.current,
        tabAwayMs: tabAwayMsRef.current, // Pass tabAwayMs
        hiddenTotalMs: closedTotalMsRef.current, // Pass closedTotalMs as penalty
        notFocusedMs: notFocusedMsRef.current,
        drowsyMs: drowsyMsRef.current,
        postureMs: postureMsRef.current,
        yawnPenaltyMs: yawnPenaltyMsRef.current,
        sustainedFocusMs: sustainedFocusMsRef.current,
        totalFocusMs: totalFocusMsRef.current,
        elapsedMs: elapsedMsCalc, // Current elapsed time
        totalSessionMs: totalSessionMsCalc, // Target duration
      }, weights);
      const ach = computeAchievements({
        phoneMs: phoneMsRef.current,
        awayMs: awayMsRef.current,
        notFocusedMs: notFocusedMsRef.current,
        drowsyMs: drowsyMsRef.current,
        postureMs: postureMsRef.current,
        sustainedFocusMs: sustainedFocusMsRef.current,
        totalFocusMs: totalFocusMsRef.current,
      }, score);
      
      focusConfidenceSeriesRef.current.push({ t: Date.now(), v: score });
      if (focusConfidenceSeriesRef.current.length > 300) focusConfidenceSeriesRef.current.shift();
      const out = {
        sessionSeconds: (sm as any)?.sessionSeconds || 0,
        distractions: merged,
        percentFocused: (sm as any)?.percentFocused || 0,
        awaySeconds: Math.floor(awayMsRef.current / 1000),
        phoneSeconds: Math.floor(phoneMsRef.current / 1000),
        drowsySeconds: Math.floor(drowsyMsRef.current / 1000),
        postureSeconds: Math.floor(postureMsRef.current / 1000),
        notFocusedSeconds: Math.floor(notFocusedMsRef.current / 1000),
        cameraOffSeconds: Math.floor(cameraOffMsRef.current / 1000),
        poorQualitySeconds: Math.floor(poorQualityMsRef.current / 1000),
        tooFarSeconds: Math.floor(tooFarMsRef.current / 1000),
        orientFailSeconds: Math.floor(orientFailMsRef.current / 1000),
        lookDownSeconds: Math.floor(lookDownMsRef.current / 1000),
        focusScore: score,
        focusPenalties: typeof penalties === 'number' ? Math.round(penalties) : undefined,
        focusRewards: typeof rewards === 'number' ? Math.round(rewards) : undefined,
        penaltyBreakdown: breakdown,
        focusScoreSeries: focusConfidenceSeriesRef.current,
        focusHistory: focusHistoryRef.current,
        sustainedBlocks: blocks,
        achievements: ach,
        contrastSeries: contrastSeriesRef.current,
        distanceSeries: distanceSeriesRef.current,
        activeSeconds: Math.floor(Math.max(0, (elapsedMsCalc || 0) - closedTotalMsRef.current) / 1000),
        totalFocusSeconds: Math.floor(totalFocusMsRef.current / 1000),
        lastUpdated: Date.now(),
      };
      localStorage.setItem(`sessionStats:${sid}`, JSON.stringify(out));
      const nowPost = Date.now();
      const lp = (window as any).__lastMetricsPost || 0;
      if (nowPost - lp > 5000) {
        try {
          const uid = localStorage.getItem('userId');
          if (uid) {
            const payload: any = {
              userId: Number(uid),
              percent_focused: Math.round(out.focusScore || 0),
              total_focus_seconds: Math.floor(totalFocusMsRef.current / 1000),
              away_seconds: out.awaySeconds || 0,
              phone_seconds: out.phoneSeconds || 0,
              drowsy_seconds: out.drowsySeconds || 0,
              posture_seconds: out.postureSeconds || 0,
              not_focused_seconds: out.notFocusedSeconds || 0,
              hidden_seconds: Math.floor(closedTotalMsRef.current / 1000),
              tab_away_seconds: Math.floor(tabAwayMsRef.current / 1000),
              summary_para1: null,
              summary_para2: null
            };
            api.post(`/session/${sid}/metrics`, payload, { skipAuthRedirect: true } as any).catch((e) => console.error('[SyncCheck] Post error', e));
            (window as any).__lastMetricsPost = nowPost;
          }
        } catch (e) {
             console.error('[SyncCheck] Inner error', e);
        }
      }
      const d = new Date();
      const dateKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const dailyRaw = localStorage.getItem('focusDaily:v1');
      const daily = dailyRaw ? JSON.parse(dailyRaw) : {};
      daily[dateKey] = score;
      localStorage.setItem('focusDaily:v1', JSON.stringify(daily));
    } catch (err) {
      console.error('[SyncCheck] Error:', err);
    }
  }, [sm, isRestored, sessionId]);

  const refreshCameras = useCallback(async () => {
    try {
      const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      tmp.getTracks().forEach(t => t.stop());
    } catch {}
    try {
      const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '').toLowerCase() : '';
      const isIOS = /iphone|ipad|ipod/.test(ua);
      if (isIOS) {
        const synthetic: any[] = [
          { deviceId: 'facing:user', kind: 'videoinput', label: 'Front Camera', groupId: '' },
          { deviceId: 'facing:environment', kind: 'videoinput', label: 'Back Camera', groupId: '' },
        ];
        setVideoDevices(synthetic as any);
        if (!selectedDeviceId) setSelectedDeviceId('facing:user');
      } else {
        const all = await navigator.mediaDevices.enumerateDevices();
        const vids = all.filter(d => d.kind === 'videoinput');
        let front: MediaDeviceInfo | undefined = vids.find(d => /front|user/i.test(d.label || ''));
        let back: MediaDeviceInfo | undefined = vids.find(d => /back|rear|environment/i.test(d.label || ''));
        if (!front && vids.length > 0) front = vids[0];
        if (!back && vids.length > 1) {
          back = vids.find(d => d.deviceId !== (front && front.deviceId)) || vids[1];
        }
        const filtered = [front, back].filter(Boolean) as MediaDeviceInfo[];
        setVideoDevices(filtered);
        const hasSelected = filtered.some(d => d.deviceId === (selectedDeviceId || ''));
        if (!hasSelected) {
          const defaultId = (front && front.deviceId) || (filtered[0] && filtered[0].deviceId) || '';
          setSelectedDeviceId(prev => prev || defaultId);
        }
      }
    } catch {}
  }, [selectedDeviceId]);
  useEffect(() => { refreshCameras(); }, [refreshCameras]);
  useEffect(() => {
    try {
      (window as any).__cameraControls = {
        setBreak: (val: boolean) => { const v = !!val; setOnBreak(v); setStarted(!v); },
        togglePiP: () => toggleComposedPiP(),
        refreshCameras: () => refreshCameras(),
        setCamera: (id: string) => setSelectedDeviceId(id || ''),
      };
    } catch {}
  }, [toggleComposedPiP, refreshCameras]);
  const content = (
    <div className="w-100 h-100 p-2" style={{ border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px' }}>
      
      <div style={{ position: 'relative', width: '100%', maxWidth: 'none', height: '100%' }}>
        <video
          ref={videoRef}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: 'auto',
            opacity: 0,
            filter: pipComposedActive ? 'grayscale(1) brightness(0.3)' : 'none',
          }}
          playsInline
          muted
        />
        <canvas ref={canvasRef} style={{ width: '100%', height: 'auto', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }} />
        <canvas ref={pipCanvasRef} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} aria-hidden="true" />
        <video ref={pipVideoRef} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} playsInline muted aria-hidden="true" />
        {null}
        {errorMsg && (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#ff6b6b', background: 'rgba(0,0,0,0.85)', padding: '12px', borderRadius: '8px', textAlign: 'center', zIndex: 10 }}>
            {errorMsg}
          </div>
        )}
        {onBreak && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(96,96,96,0.65)', borderRadius: 8, pointerEvents: 'none' }} aria-hidden="true" />
        )}
        
        <div style={{ position: 'absolute', left: 8, bottom: 16 }}>
          <button className="btn btn-pip-sm btn-pip-toggle" disabled={workflowStage !== 'ready'} onClick={() => toggleComposedPiP()}>PiP Mode</button>
          {pipMsg && (
            <div style={{ 
              position: 'absolute', 
              bottom: '100%', 
              left: 0, 
              marginBottom: 8, 
              background: 'rgba(0,0,0,0.8)', 
              color: '#fff', 
              padding: '4px 8px', 
              borderRadius: 4, 
              fontSize: '0.75rem',
              whiteSpace: 'nowrap'
            }}>
              {pipMsg}
            </div>
          )}
        </div>
        <div style={{ position: 'absolute', right: 8, top: 8 }}>
          <select
            className="form-select form-select-sm"
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            style={{ minWidth: 200 }}
            aria-label="Select camera"
          >
            <option value="">Default camera</option>
            {videoDevices.map((d, idx) => (
              <option key={d.deviceId || idx} value={d.deviceId || ''}>{d.label || `Camera ${idx + 1}`}</option>
            ))}
          </select>
        </div>

      </div>
      
      
    </div>
  );

  return content;
};

export default CameraMonitor;
